import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, RefreshCw, X } from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';
import { Analytics } from '../utils/analytics';

interface ServiceUnavailableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
  serviceName: string;
  retryInSeconds?: number;
}

const ServiceUnavailableModal: React.FC<ServiceUnavailableModalProps> = ({
  isOpen,
  onClose,
  onRetry,
  serviceName,
  retryInSeconds = 300 // 5 minutes default
}) => {
  const { t } = useLocalization();
  const [countdown, setCountdown] = useState(retryInSeconds);

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Auto retry when countdown reaches 0
          Analytics.autoRetryAttempt(serviceName);
          onRetry();
          return retryInSeconds;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onRetry, retryInSeconds, serviceName]);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(retryInSeconds);
    }
  }, [isOpen, retryInSeconds]);

  const handleManualRetry = () => {
    Analytics.manualRetryAttempt(serviceName);
    onRetry();
  };

  const handleCancel = () => {
    Analytics.retryCancelled(serviceName);
    onClose();
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t.errors.serviceUnavailable.title}
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            {t.errors.serviceUnavailable.message.replace('{service}', serviceName)}
          </p>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">
                {t.errors.serviceUnavailable.autoRetryIn}
              </span>
            </div>
            
            <div className="text-2xl font-mono font-bold text-orange-600 mb-2">
              {formatTime(countdown)}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleManualRetry}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t.errors.serviceUnavailable.retryNow}
          </button>
          
          <button
            onClick={handleCancel}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            {t.errors.serviceUnavailable.cancel}
          </button>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            {t.errors.serviceUnavailable.helpText}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceUnavailableModal;
